import { useEffect, useState } from "react";
import { onValue, ref, get, child } from "firebase/database";
import { rtdb } from "../../../../firebase.config";

export default function useLandlordApplications(landlordUid) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  useEffect(() => {
    if (!landlordUid) return;

    const appsRef = ref(rtdb, "tenant_property_applications");

    const unsub = onValue(
      appsRef,
      async (snap) => {
        try {
          const all = snap.val() || {};
          const mine = Object.values(all).filter(
            (a) => a.landlordId === landlordUid
          );

          const enriched = await Promise.all(
            mine.map(async (a) => {
              const id = a.applicationId || a.id;

              let tenantName = a.applicantName || "Applicant";
              let avatar = "";
              try {
                const profileSnap = await get(
                  child(ref(rtdb), `tenant_profiles/${a.tenantId}`)
                );
                if (profileSnap.exists()) {
                  const p = profileSnap.val() || {};
                  tenantName = p.name || tenantName;
                  avatar = p.profileImage || avatar;
                }
              } catch (_) {}

              let email = "",
                phone = a.phone || "";
              try {
                const userSnap = await get(
                  child(ref(rtdb), `tenant_users/${a.tenantId}`)
                );
                if (userSnap.exists()) {
                  const u = userSnap.val() || {};
                  email = u.email || email;
                  phone = u.phoneNumber || phone;
                }
              } catch (_) {}

              let propertyTitle = a.propertyTitle || "";
              try {
                const propSnap = await get(
                  child(ref(rtdb), `properties/${landlordUid}/${a.propertyId}`)
                );
                if (propSnap.exists()) {
                  const p = propSnap.val() || {};
                  propertyTitle = p.title || propertyTitle;
                }
              } catch (_) {}

              // Check lease table for status override
              let finalStatus = (
                a.status ||
                a.applicationStatus ||
                "submitted"
              ).toLowerCase();
              try {
                const leaseSnap = await get(child(ref(rtdb), `leases/${id}`));
                if (leaseSnap.exists()) {
                  const lease = leaseSnap.val();
                  if (lease.status === "sent") {
                    finalStatus = "lease Sent";
                  } else if (lease.status === "signed") {
                    finalStatus = "lease Signed";
                  } else if (lease.status === "rejected") {
                    finalStatus = "rejected";
                  }
                }
              } catch (_) {}

              // Score calculation
              const requiredFields = [
                "applicantName",
                "dateOfBirth",
                "email",
                "phone",
                "currentAddress",
                "annualIncome",
                "employer",
                "referenceName",
              ];
              const provided = requiredFields.filter((f) => !!a[f]).length;
              const score =
                requiredFields.length > 0
                  ? Math.round((provided / requiredFields.length) * 100)
                  : 0;

              return {
                key: id,
                id,
                unit: a.propertyId || "-",
                applicantName: tenantName,
                avatar,
                email,
                phone,
                date: a.applicationDate,
                amount: a.rent ? `$${a.rent}` : "",
                method:
                  a.paymentType === "landlord"
                    ? "Card Payment"
                    : a.paymentType || "—",
                agent: "",
                property: propertyTitle || a.propertyAddress || "—",
                status: finalStatus.replace(/^\w/, (c) => c.toUpperCase()),
                score,
                raw: a,
              };
            })
          );

          setRows(enriched);
          setLoading(false);
        } catch (e) {
          setErr(e);
          setLoading(false);
        }
      },
      (e) => {
        setErr(e);
        setLoading(false);
      }
    );

    return () => unsub();
  }, [landlordUid]);

  return { rows, loading, error: err };
}
